// Official GPT sample ad unit; it never generates publisher revenue.
export const TEST_REWARDED_AD_UNIT = '/22639388115/rewarded_web_example';
export const DOWNLOAD_REWARDED_AD_UNIT = '/23390887626/recording_download_rewarded';

let tagPromise;
let activeRequest = false;

export function loadPublisherTag() {
  if (window.googletag?.apiReady) return Promise.resolve(window.googletag);
  if (tagPromise) return tagPromise;

  tagPromise = new Promise((resolve, reject) => {
    window.googletag = window.googletag || {};
    window.googletag.cmd = window.googletag.cmd || [];
    let script = document.querySelector('script[data-recording-gpt]');
    const timer = setTimeout(() => fail(), 12000);
    const fail = () => {
      clearTimeout(timer);
      script?.remove();
      reject(new Error('GPT_LOAD_FAILED'));
    };
    window.googletag.cmd.push(() => {
      clearTimeout(timer);
      resolve(window.googletag);
    });
    if (!script) {
      script = document.createElement('script');
      script.async = true;
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      script.crossOrigin = 'anonymous';
      script.dataset.recordingGpt = 'true';
      script.addEventListener('error', fail, { once: true });
      document.head.appendChild(script);
    }
  }).catch((error) => {
    tagPromise = undefined;
    throw error;
  });
  return tagPromise;
}

// Returns a disposer immediately, including while GPT is still loading.
// Grant and close are separate events: only report earned after the user closes.
export function requestRewardedAd({
  adUnitPath, onReady, onResult,
  loadTag = loadPublisherTag, timeoutMs = 15000,
}) {
  if (activeRequest) {
    queueMicrotask(() => onResult('busy'));
    return () => {};
  }
  activeRequest = true;
  let disposed = false;
  let slot;
  let tag;
  let pubads;
  let granted = false;
  let showing = false;
  const listeners = [];
  const timer = setTimeout(() => finish('timeout'), timeoutMs);

  function cleanup() {
    if (disposed) return;
    disposed = true;
    clearTimeout(timer);
    listeners.forEach(([name, listener]) => pubads.removeEventListener(name, listener));
    if (slot) tag.destroySlots([slot]);
    activeRequest = false;
  }

  function finish(result) {
    if (disposed) return;
    cleanup();
    onResult(result);
  }

  function listen(name, handler) {
    const listener = (event) => {
      if (!disposed && event.slot === slot) handler(event);
    };
    listeners.push([name, listener]);
    pubads.addEventListener(name, listener);
  }

  Promise.resolve().then(loadTag).then((loadedTag) => {
    if (disposed) return;
    tag = loadedTag;
    slot = tag.defineOutOfPageSlot(adUnitPath, tag.enums.OutOfPageFormat.REWARDED);
    if (!slot) return finish('unsupported');
    pubads = tag.pubads();
    slot.addService(pubads);
    listen('slotRenderEnded', (event) => {
      if (event.isEmpty) finish('empty');
    });
    listen('rewardedSlotGranted', () => { if (showing) granted = true; });
    listen('rewardedSlotClosed', () => finish(granted ? 'earned' : 'cancelled'));
    listen('rewardedSlotReady', (event) => {
      clearTimeout(timer);
      onReady(() => {
        if (disposed || showing) return;
        showing = true;
        try {
          if (event.makeRewardedVisible() === false) finish('unavailable');
        } catch {
          finish('unavailable');
        }
      });
    });
    tag.enableServices();
    tag.display(slot);
  }).catch((error) => {
    if (!disposed) console.warn('Rewarded ad request failed:', error);
    finish('load-failed');
  });

  return cleanup;
}
